import React, { useRef, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import Branded from '../components/Branded';

const Section = styled.section`
  background: #fff;
  padding: 100px 0;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 64px 0;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 56px;

  .tag {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #f08020;
    margin-bottom: 12px;
  }

  h2 {
    font-size: 2.2rem;
    font-weight: 700;
    color: #111;
    line-height: 1.2;

    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }
`;

const ScrollWrap = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  cursor: grab;
  user-select: none;

  &:active {
    cursor: grabbing;
  }
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Track = styled.div`
  display: flex;
  gap: 24px;
  min-width: min-content;
`;

const Card = styled.div`
  flex: 0 0 calc((100% - 72px) / 4);
  text-align: center;

  @media (max-width: 1024px) {
    flex: 0 0 calc((100% - 48px) / 3);
  }

  @media (max-width: 768px) {
    flex: 0 0 calc((100% - 24px) / 2);
  }

  @media (max-width: 540px) {
    flex: 0 0 78%;
  }

  .card-screenshot {
    width: 100%;
    aspect-ratio: 1 / 1;
    border-radius: 16px;
    overflow: hidden;
    background: #fff;
    border: 3px solid #f08020;
    margin-bottom: 20px;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  }

  .card-content {
    width: 100%;
    min-height: 260px;
    border-radius: 16px;
    background: #fff;
    border: 3px solid #f08020;
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 22px 20px;
    gap: 12px;

    .card-avatar {
      width: 64px;
      height: 64px;
      border-radius: 50%;
      overflow: hidden;
      border: 2px solid #d8e1ec;
      background: #1378e5;
      flex-shrink: 0;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
    }

    .card-stars {
      display: flex;
      gap: 3px;
    }

    .star svg {
      width: 14px;
      height: 14px;
    }

    .card-text {
      font-size: 13px;
      color: #1b2733;
      line-height: 1.65;
      display: -webkit-box;
      -webkit-line-clamp: 5;
      -webkit-box-orient: vertical;
      overflow: hidden;
      margin: 0;
    }
  }

  .card-name {
    font-size: 15px;
    font-weight: 600;
    color: #1378e5;
    margin-bottom: 4px;
  }

  .card-position {
    font-size: 12px;
    color: #5b6b7c;
    line-height: 1.4;
  }

  .card-company {
    font-size: 12px;
    color: #f08020;
    margin-top: 2px;
  }
`;

const ScrollIndicator = styled.div`
  margin-top: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;

  .hint {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #5b6b7c;
    display: inline-flex;
    align-items: center;
    gap: 8px;

    svg {
      width: 14px;
      height: 14px;
      stroke: #f08020;
      fill: none;
      stroke-width: 1.6;
      stroke-linecap: round;
      stroke-linejoin: round;
      animation: drag-hint 1.6s ease-in-out infinite;
    }
  }

  .track {
    width: 220px;
    height: 2px;
    background: rgba(19, 120, 229, 0.12);
    position: relative;
    overflow: hidden;
  }

  .thumb {
    height: 100%;
    background: #f08020;
    transition: width 0.15s ease;
  }

  @keyframes drag-hint {
    0%,
    100% {
      transform: translateX(0);
    }
    50% {
      transform: translateX(4px);
    }
  }

  @media (max-width: 540px) {
    .track {
      width: 140px;
    }
    .hint {
      font-size: 10px;
    }
  }
`;

export default function Testimonials({ tag, title, testimonials }) {
  const data = testimonials || [];
  const scrollRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  const [scrollPct, setScrollPct] = useState(0);
  const [isScrollable, setIsScrollable] = useState(false);

  const updateScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setIsScrollable(max > 0);
    setScrollPct(max > 0 ? (el.scrollLeft / max) * 100 : 0);
  }, []);

  const onMouseDown = useCallback(e => {
    isDragging.current = true;
    startX.current = e.pageX - scrollRef.current.offsetLeft;
    scrollLeft.current = scrollRef.current.scrollLeft;
  }, []);

  const onMouseMove = useCallback(e => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    scrollRef.current.scrollLeft =
      scrollLeft.current - (x - startX.current) * 1.5;
  }, []);

  const onMouseUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  useEffect(() => {
    document.addEventListener('mouseup', onMouseUp);
    return () => document.removeEventListener('mouseup', onMouseUp);
  }, [onMouseUp]);

  useEffect(() => {
    updateScroll();
    window.addEventListener('resize', updateScroll);
    return () => window.removeEventListener('resize', updateScroll);
  }, [updateScroll, data.length]);

  if (!data.length) return null;

  return (
    <Section>
      <Container>
        <Header>
          {tag && <div className="tag">{tag}</div>}
          <h2>{title || 'What Our Clients Are Saying'}</h2>
        </Header>

        <ScrollWrap
          ref={scrollRef}
          onScroll={updateScroll}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
        >
          <Track>
            {data.map(t => {
              const screenshotUrl = t.screenshot?.asset?.url;
              const photoUrl = t.photo?.asset?.url;
              const hasContent = !!t.content;
              const hasRating = typeof t.rating === 'number' && t.rating > 0;
              const showAsScreenshot =
                !!screenshotUrl && !hasContent && !photoUrl;
              const avatarUrl = photoUrl || screenshotUrl;

              return (
                <Card key={t._id}>
                  {showAsScreenshot ? (
                    <div className="card-screenshot">
                      <img
                        src={screenshotUrl + '?w=400&auto=format'}
                        alt={`${t.name} review`}
                      />
                    </div>
                  ) : (
                    <div className="card-content">
                      {avatarUrl && (
                        <div className="card-avatar">
                          <img
                            src={avatarUrl + '?w=200&auto=format'}
                            alt={t.name}
                          />
                        </div>
                      )}
                      {(hasRating || !hasContent) && (
                        <div className="card-stars">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <span key={i} className="star">
                              <svg
                                viewBox="0 0 20 20"
                                fill={
                                  i < (t.rating || 5)
                                    ? '#f5c518'
                                    : 'rgba(0,0,0,0.15)'
                                }
                              >
                                <path d="M10 1l2.39 4.84L17.82 6.8l-3.91 3.81.92 5.39L10 13.42 5.17 16l.92-5.39L2.18 6.8l5.43-.96L10 1z" />
                              </svg>
                            </span>
                          ))}
                        </div>
                      )}
                      {hasContent && (
                        <Branded
                          as="div"
                          className="card-text"
                          text={t.content}
                        />
                      )}
                    </div>
                  )}
                  <div className="card-name">{t.name}</div>
                  {t.position && (
                    <div className="card-position">{t.position}</div>
                  )}
                  {t.company && <div className="card-company">{t.company}</div>}
                </Card>
              );
            })}
          </Track>
        </ScrollWrap>

        {isScrollable && (
          <ScrollIndicator>
            <span className="hint">
              Drag to view more
              <svg viewBox="0 0 24 24">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </span>
            <div className="track">
              <div
                className="thumb"
                style={{
                  width: '24%',
                  marginLeft: `${(scrollPct * 76) / 100}%`,
                }}
              />
            </div>
          </ScrollIndicator>
        )}
      </Container>
    </Section>
  );
}
